import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ExclamationTriangleIcon,
  TrashIcon,
  ArrowPathIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { fetchWithAuth } from '../services/api';

const ProfileDeleteModal = ({ profile, isOpen, onClose, onDelete }) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      const endpoint = profile.isAdmin ? `/admin/profiles/${profile.stringId || profile.id}` : `/profiles/${profile.stringId || profile.id}`;
      await fetchWithAuth(endpoint, {
        method: 'DELETE',
      });

      if (onDelete) {
        onDelete(profile.id);
      }
      onClose();
    } catch (err) {
      console.error('Error deleting profile:', err);
      setError(err.message || t('profileDelete.error.deleteFailed', 'Failed to delete profile.'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!isOpen || !profile) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="bg-red-600 px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-white mr-2" />
              <h2 className="text-xl font-bold text-white" data-testid="profile-delete-modal-title">
                {t('profileDelete.title', 'Delete Profile')}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-gray-200 transition-colors"
              data-testid="profile-delete-modal-close"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm" data-testid="profile-delete-error">{error}</p>
            </div>
          )}

          <div className="mb-6">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mr-4" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('profileDelete.warning', 'This profile will no longer be accessible')}
                </h3>
                <p className="text-gray-600">
                  {t('profileDelete.warningText', 'The profile will be marked as deleted and its QR code will be unbound for reuse.')}
                </p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700 mb-2">
                {t('profileDelete.profileToDelete', 'Profile to delete:')}
              </p>
              <p className="font-semibold text-gray-900" data-testid="profile-delete-name">
                {profile.name}
              </p>
              {profile.birthDate && profile.deathDate && (
                <p className="text-sm text-gray-600">
                  {profile.birthDate} - {profile.deathDate}
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons - Made smaller */}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isDeleting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 transition-colors"
              data-testid="profile-delete-cancel"
            >
              {t('profileDelete.cancel')}
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex-1 sm:flex-none px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              data-testid="profile-delete-confirm"
            >
              {isDeleting ? (
                <>
                  <ArrowPathIcon className="animate-spin h-4 w-4 mr-2" />
                  {t('profileDelete.deleting', 'Deleting...')}
                </>
              ) : (
                <>
                  <TrashIcon className="h-4 w-4 mr-2" />
                  {t('profileDelete.deleteButton', 'Delete Profile')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileDeleteModal;
